// Web-only Firebase init. Loaded lazily from `analytics.ts` so the JS SDK
// never lands in native bundles (the native side uses @react-native-firebase).
//
// Config is read from EXPO_PUBLIC_FIREBASE_* env vars and inlined at build
// time. Firebase web config values are not secrets — they identify the
// project; security comes from Firebase Security Rules and API key
// restrictions in the Google Cloud console.

import { type FirebaseApp, initializeApp } from 'firebase/app';
import {
  type Analytics,
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  setUserProperties,
} from 'firebase/analytics';

export type WebAnalyticsHandle = {
  logEvent: (name: string, params?: Record<string, string | number>) => void;
  setUserId: (id: string | null) => void;
  setUserProperty: (name: string, value: string | null) => void;
};

const config = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;

// Lazily init (and cache) the web Firebase app, shared by analytics and auth
// so we never spin up two instances. Auth only needs apiKey + appId (not the
// analytics measurementId), so this guards on those.
export const getFirebaseWebApp = (): FirebaseApp | null => {
  if (!config.apiKey || !config.appId) return null;
  try {
    app ??= initializeApp(config);
    return app;
  } catch {
    return null;
  }
};

export const initFirebaseWebAnalytics = async (): Promise<WebAnalyticsHandle | null> => {
  if (!config.apiKey || !config.measurementId || !config.appId) {
    if (__DEV__) {
      console.warn(
        '[analytics] Firebase web config missing — set EXPO_PUBLIC_FIREBASE_* env vars to enable web analytics.'
      );
    }
    return null;
  }

  try {
    if (!(await isSupported())) return null;
    app ??= getFirebaseWebApp();
    if (!app) return null;
    analytics ??= getAnalytics(app);
  } catch (err) {
    if (__DEV__) {
      console.warn('[analytics] Firebase web init failed:', err);
    }
    return null;
  }

  return {
    logEvent: (name, params) => {
      if (analytics) logEvent(analytics, name, params);
    },
    setUserId: (id) => {
      if (analytics) setUserId(analytics, id ?? '');
    },
    setUserProperty: (name, value) => {
      if (analytics) setUserProperties(analytics, { [name]: value });
    },
  };
};

export default {};
